/*
 * The core event union, hand-written: one member per `←` row that core owns in protocol.md
 * §2.5.1, §2.5.2 and §2.5.8. tygo emits `string` for a Go const block, so the discriminated union
 * that makes applyEvent exhaustive cannot be generated (SPIKE §2.3); the payload types are the
 * generated ones, and only the pairing of a type string with its payload lives here.
 *
 * Two shapes. A durable event carries the `eventId` the change log gave it and is what `rt.sync`
 * replays; an ephemeral one carries none — that absence is the marker (protocol.md §2.4) — and is
 * never replayed. `conv` is present on the envelope where the payload is about one conversation.
 */

import type * as core from "./core.gen.ts"

/** A frame the change log holds: every durable event has an id, and `rt.sync` resumes from it. */
export interface DurableEvent<T extends string, D> {
	readonly type: T
	readonly eventId: number
	readonly conv?: number
	readonly seq?: number
	readonly data: D
}

/** A frame that only ever travels live; `eventId` is never set so the gap rule ignores it. */
export interface EphemeralEvent<T extends string, D> {
	readonly type: T
	readonly eventId?: undefined
	readonly conv?: number
	readonly seq?: undefined
	readonly data: D
}

export type CoreEvent =
	| EphemeralEvent<"rt.resync", core.Resync>
	| EphemeralEvent<"rt.draining", core.Draining>
	// Built by the transport as it closes the connection, never through the change log, so it
	// carries no eventId (protocol.md §2.4, hub/revoke.go).
	| EphemeralEvent<"session.revoked", core.SessionRevoked>
	| DurableEvent<"account.updated", core.AccountUpdated>
	| DurableEvent<"account.group.changed", core.GroupChanged>
	| DurableEvent<"policy.changed", core.PolicyChanged>
	| DurableEvent<"settings.changed", core.SettingsChanged>
	| DurableEvent<"ui.settings.changed", core.UiSettingsChanged>
	| DurableEvent<"activity.item", core.ActivityItem>
	| DurableEvent<"activity.read", core.ActivityRead>
	| DurableEvent<"notify.hint", core.NotifyHint>
	| DurableEvent<"notify.prefs.changed", core.NotificationPrefView>
	| DurableEvent<"file.scanChanged", core.FileScanChanged>

export type CoreEventType = CoreEvent["type"]

/**
 * The same set as a value, for the checks that run at runtime — the fixture test and the frame
 * decoder. `satisfies Record<CoreEventType, true>` makes the object and the union agree in both
 * directions: a member added above without a key here, or a key here without a member, is a
 * compile error.
 */
const CORE_EVENT_TYPE_SET = {
	"rt.resync": true,
	"rt.draining": true,
	"session.revoked": true,
	"account.updated": true,
	"account.group.changed": true,
	"policy.changed": true,
	"settings.changed": true,
	"ui.settings.changed": true,
	"activity.item": true,
	"activity.read": true,
	"notify.hint": true,
	"notify.prefs.changed": true,
	"file.scanChanged": true,
} as const satisfies Record<CoreEventType, true>

export const CORE_EVENT_TYPES = Object.keys(CORE_EVENT_TYPE_SET) as readonly CoreEventType[]

export function isCoreEventType(type: string): type is CoreEventType {
	return Object.prototype.hasOwnProperty.call(CORE_EVENT_TYPE_SET, type)
}

/** Narrows an event of the aggregate union to core's own; everything else belongs to a module. */
export function isCoreEvent(event: { readonly type: string }): event is CoreEvent {
	return isCoreEventType(event.type)
}
